import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { 
  getTenantId, 
  parseArrayData, 
  isPrimaryOccupant,
  getAssignedBuildings,
  getMaintenanceCategories,
  getMaintenanceRequests,
  submitMaintenanceRequest,
  uploadProfileImage,
  normalizePriority,
} from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import { RequestModal, normalizeRequest } from "../components/RequestModal";
import toast from "react-hot-toast";
import { applyEnumText } from "../../utils/enums";

const PAGE_SIZE = 15;

// ─── Helpers ─────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const PRIORITY_COLORS = {
  critical: "#DC2626",
  high: "#E53E3E",
  medium: "#D97706",
  low: "#16A34A",
};

const STATUS_COLORS = {
  created: "#2563EB",
  open: "#2563EB",
  "in-progress": "#D97706",
  inprogress: "#D97706",
  resolved: "#16A34A",
  closed: "#718096",
};

function getPriorityColor(priority) {
  const p = (priority || "").toLowerCase();
  return PRIORITY_COLORS[p] || "#718096";
}

function getStatusColor(status) {
  const s = (status || "").toLowerCase();
  return STATUS_COLORS[s] || "#718096";
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const BLANK_FORM = { title: "", description: "", categoryId: "", priorityLevel: "Medium", buildingId: "", fileUploadUrls: [] };

// ─── New Request Modal ───────────────────────────────────────
export function NewRequestModal({ modalRef, categories, buildings, onSave, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const tooBig = files.find((f) => f.size > 5 * 1024 * 1024);
    if (tooBig) {
      toast.error("Each photo must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const url = await uploadProfileImage(file);
        if (url) urls.push(url);
      }
      setForm((p) => ({ ...p, fileUploadUrls: [...p.fileUploadUrls, ...urls] }));
    } catch (err) {
      toast.error(err.message || "Photo upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => setForm((p) => ({ ...p, fileUploadUrls: p.fileUploadUrls.filter((u) => u !== url) }));

  const field = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleClose = useCallback(() => {
    hide();
    setForm(BLANK_FORM);
    onClose?.();
  }, [hide, onClose]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Please provide a title for the issue.");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Please describe the maintenance issue.");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      handleClose();
    } catch {
      // Handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-wrench"
      title="New Maintenance Request"
      onClose={handleClose}
      closeDisabled={saving}
      width={640}
      subheader={
        <div className="px-6 py-2.5 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">Submit an issue or repair ticket for estate facility response</span>
          <StatusChip color="#0EA84A">Tenant Portal</StatusChip>
        </div>
      }
      footer={
        <div className="d-flex align-items-center justify-content-between w-100">
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-success d6-mbtn px-5" onClick={handleSave} disabled={saving || uploading}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Submitting…
              </>
            ) : (
              <>
                <i className="ki-outline ki-check-circle fs-5 me-2.5" />
                Submit Request
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-4">
        <Field label="Issue Title" required hint="e.g. Leaking Kitchen Sink, AC not cooling, Main gate jammed">
          <input
            type="text"
            className="d6m-input"
            placeholder="Brief summary of the issue"
            value={form.title}
            onChange={field("title")}
          />
        </Field>

        <div className="row g-3">
          <div className="col-12 col-md-6">
            <Field label="Category" hint="Select service specialization">
              <select className="d6m-input" value={form.categoryId} onChange={field("categoryId")}>
                <option value="">Select category…</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Priority Level" hint="Urgency of repair">
              <select className="d6m-input" value={form.priorityLevel} onChange={field("priorityLevel")}>
                {["Low", "Medium", "High", "Critical"].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <Field label="Property / Building Unit" hint="Select the residence location where repair is needed">
          <select className="d6m-input" value={form.buildingId} onChange={field("buildingId")}>
            <option value="">Select residence…</option>
            {buildings.map(b => {
              const id = b.buildingId || b.id;
              const label = [
                b.phaseName,
                b.number ? `House ${b.number}` : (b.buildingName || b.name),
                b.typeName ? `(${b.typeName})` : ""
              ].filter(Boolean).join(" — ");
              return (
                <option key={id} value={id}>
                  {label || "Building"}
                </option>
              );
            })}
          </select>
        </Field>

        <Field label="Detailed Description" required hint="Provide details, symptoms, exact location, or access instructions">
          <textarea
            className="d6m-input"
            rows={4}
            placeholder="Please describe what is happening in detail…"
            value={form.description}
            onChange={field("description")}
          />
        </Field>

        <Field label="Photos" hint="Optional · PNG or JPG, up to 5MB each">
          <input ref={photoRef} type="file" accept="image/*" multiple className="d-none" onChange={handlePhotos} />
          {form.fileUploadUrls.length > 0 && (
            <div className="d6m-attachments mb-3">
              {form.fileUploadUrls.map((url) => (
                <div key={url} className="d6m-thumb position-relative">
                  <img src={url} alt="Attachment" />
                  <button
                    type="button"
                    className="d6m-x position-absolute top-0 end-0 m-1"
                    style={{ width: 24, height: 24, background: "rgba(255,255,255,0.9)" }}
                    onClick={() => removePhoto(url)}
                    aria-label="Remove photo"
                  >
                    <i className="ki-outline ki-cross" style={{ fontSize: "0.9rem" }}></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div
            className="d6-card p-4 d-flex flex-column align-items-center justify-content-center border border-dashed border-gray-300 bg-hover-light cursor-pointer"
            style={{ minHeight: "110px" }}
            onClick={() => !uploading && photoRef.current?.click()}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm text-success mb-2" />
                <span className="fs-8 fw-semibold text-gray-800">Uploading…</span>
              </>
            ) : (
              <>
                <div className="symbol symbol-45px symbol-circle bg-light-success text-success d-flex align-items-center justify-content-center mb-2">
                  <i className="ki-outline ki-camera fs-3 text-success" />
                </div>
                <span className="fs-8 fw-semibold text-gray-800">Click to upload photos</span>
                <span className="fs-9 text-gray-400 mt-0.5">Show the estate team what needs fixing</span>
              </>
            )}
          </div>
        </Field>
      </div>
    </ModalShell>
  );
}

// ─── Main Component ──────────────────────────────────────────
export default function TenantMaintenance() {
  const dashboardRef = useRef(null);
  const tenantId = getTenantId();
  const isOccupant = isPrimaryOccupant();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);

  const newRequestModalRef = useRef(null);
  const detailModalRef = useRef(null);

  const newRequestModal = useBootstrapModal(newRequestModalRef);
  const detailModal = useBootstrapModal(detailModalRef);

  const fetchAll = useCallback(async (pg = 0) => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [cRes, bRes, mRes] = await Promise.allSettled([
        getMaintenanceCategories(tenantId),
        getAssignedBuildings({ pageIndex: 1, pageSize: 100, id: tenantId }),
        getMaintenanceRequests({
          pageIndex: pg + 1,
          pageSize: PAGE_SIZE,
          search,
          status: statusFilter !== "all" ? statusFilter : "",
          id: tenantId,
        }),
      ]);

      if (cRes.status === "fulfilled") setCategories(parseArrayData(cRes.value) || []);
      if (bRes.status === "fulfilled") setBuildings(parseArrayData(bRes.value) || []);

      let fetchedRequests = [];
      if (mRes.status === "fulfilled" && mRes.value) {
        fetchedRequests = (parseArrayData(mRes.value) || []).map((r) => applyEnumText(r, ["status", "priorityLevel"]));
      }

      const visibleRequests = fetchedRequests.filter(
        (r) => categoryFilter === "all" || String(r.category || r.categoryName || "").toLowerCase() === categoryFilter.toLowerCase()
      );
      setRequests(visibleRequests.map((r) => ({ ...r, priorityLevel: normalizePriority(r.priorityLevel ?? r.priority) })));
      setTotalCount(mRes.value?.totalCount || mRes.value?.data?.totalCount || fetchedRequests.length);
      setPage(pg);
    } catch { 
      toast.error("Failed to load maintenance records."); 
    } finally { 
      setLoading(false); 
    }
  }, [tenantId, search, statusFilter, categoryFilter]);

  useEffect(() => { 
    fetchAll(0); 
  }, [fetchAll]);

  // GSAP Entrance Animations matching newdashboard2.js & TenantPayments.js
  useEffect(() => {
    if (loading) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".gsap-hero",
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" }
      );
      gsap.fromTo(
        ".gsap-card",
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" }
      );
    }, dashboardRef);
    return () => ctx.revert();
  }, [loading]);

  const handleNewRequest = async (form) => {
    try {
      await submitMaintenanceRequest({
        title: form.title,
        description: form.description,
        categoryId: form.categoryId,
        category: categories.find(c => c.id === form.categoryId)?.name || form.categoryId,
        buildingId: form.buildingId,
        priorityLevel: form.priorityLevel,
        fileUploadUrls: form.fileUploadUrls || [],
      }, tenantId);
      toast.success("Maintenance request submitted successfully.");
      fetchAll(0);
    } catch (err) {
      toast.error(err.message || "Failed to submit request.");
      throw err;
    }
  };

  const { setHeaderConfig } = useTenantHeader();

  useEffect(() => {
    setHeaderConfig({
      title: "Maintenance Management",
      breadcrumb: "Maintenance",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  const handleOpenDetail = (r) => {
    setSelected(r);
    detailModal.show();
  };

  const handleOpenNew = () => {
    newRequestModal.show();
  };

  const statuses = ["all", "Created", "Open", "In-Progress", "Resolved", "Closed"];

  const stats = useMemo(() => {
    const total = totalCount || requests.length;
    const inProgress = requests.filter(r => (r.status || "").toLowerCase().includes("progress")).length;
    const openPending = requests.filter(r => {
      const s = (r.status || "").toLowerCase();
      return s === "open" || s === "created";
    }).length;
    const resolvedClosed = requests.filter(r => {
      const s = (r.status || "").toLowerCase();
      return s === "resolved" || s === "closed" || s === "completed";
    }).length;

    return { total, inProgress, openPending, resolvedClosed };
  }, [requests, totalCount]);

  return (
    <>
      <PagePreloader visible={loading} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-6 px-lg-10">
        {/* Primary Occupant Info Notice */}
        {isOccupant && (
          <div className="d6-card p-4 mb-5 border border-primary border-dashed bg-light-primary bg-opacity-20 d-flex gap-3 align-items-center">
            <i className="ki-outline ki-information-5 fs-2 text-primary" />
            <div className="fs-8 text-gray-700">
              <strong>Facility Notice:</strong> All emergency repair tickets and scheduled facility works are managed directly by the estate operations unit.
            </div>
          </div>
        )}

        {/* ─── Hero Stats Section (Matching TenantPayments & newdashboard2) ─── */}
        <div className="d6-kpi-brand p-6 mb-5">
          <div className="row g-4 g-xl-0 align-items-center">
            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-wrench fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Total Requests</div>
                    <div className="d6-num text-white my-0.5">{stats.total}</div>
                    <div className="text-white text-opacity-70 fs-9">Submitted issues</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-arrows-circle fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">In Progress</div>
                    <div className="d6-num text-white my-0.5">{stats.inProgress}</div>
                    <div className="text-white text-opacity-70 fs-9">Being attended to</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-time fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Open / Pending</div>
                    <div className="d6-num text-white my-0.5">{stats.openPending}</div>
                    <div className="text-white text-opacity-70 fs-9">Awaiting assignment</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-check-circle fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Resolved / Closed</div>
                    <div className="d6-num text-white my-0.5">{stats.resolvedClosed}</div>
                    <div className="text-white text-opacity-70 fs-9">Completed repairs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Main Content Card ─── */}
        <div className="d6-card p-6 mb-5 gsap-card">
          <CardHead
            title="Service &amp; Maintenance"
            subtitle="Submit, track and communicate on repair requests for your property."
            className="mb-5"
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Search Bar */}
              <div className="position-relative" style={{ minWidth: 220 }}>
                <i className="ki-outline ki-magnifier fs-4 text-gray-400 position-absolute top-50 start-0 translate-middle-y ms-3" />
                <input
                  type="text"
                  className="d6m-input ps-10"
                  style={{ minHeight: 40, height: 40, borderRadius: 999 }}
                  placeholder="Search requests…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(0); }}
                />
              </div>

              {/* Status Dropdown */}
              <select
                className="d6m-input"
                style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              >
                {statuses.map(s => (
                  <option key={s} value={s}>{s === "all" ? "All Statuses" : s}</option>
                ))}
              </select>

              {/* Category Dropdown */}
              {categories.length > 0 && (
                <select
                  className="d6m-input"
                  style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
                  value={categoryFilter}
                  onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
                >
                  <option value="all">All Categories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              )}

              {/* New Request Button (Last) */}
              <button
                type="button"
                className="btn btn-success d6-mbtn d-flex align-items-center gap-2"
                onClick={handleOpenNew}
              >
                <i className="ki-outline ki-plus fs-5 me-1 text-white" />
                <span>New Request</span>
              </button>
            </div>
          </CardHead>

          {/* ─── Content List ─── */}
          {requests.length === 0 && !loading ? (
            <div className="d6m-empty py-12">
              <i className="ki-outline ki-wrench fs-3x text-gray-300 d-block mb-3"></i>
              <div className="d6-title text-gray-900 mb-1">No maintenance requests found</div>
              <div className="fs-8 text-gray-500 mb-4">
                {search || statusFilter !== "all" 
                  ? "No results matching your filters." 
                  : "You haven't submitted any maintenance tickets yet."}
              </div>
              <button
                type="button"
                className="btn btn-success d6-mbtn btn-sm"
                onClick={handleOpenNew}
              >
                <i className="ki-outline ki-plus fs-5 me-2 text-white" />
                Submit New Request
              </button>
            </div>
          ) : (
            /* List View matching TenantPayments style */
            <div className="d-flex flex-column gap-3">
              {requests.map((r, idx) => {
                const pColor = getPriorityColor(r.priorityLevel);
                const sColor = getStatusColor(r.status);
                const isResolved = (r.status || "").toLowerCase() === "resolved" || (r.status || "").toLowerCase() === "closed";
                const isHigh = (r.priorityLevel || "").toLowerCase() === "high" || (r.priorityLevel || "").toLowerCase() === "critical";

                return (
                  <div
                    key={r.id || idx}
                    onClick={() => handleOpenDetail(r)}
                    className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 border-hover-success position-relative overflow-hidden cursor-pointer transition-all"
                    style={{ minHeight: 90 }}
                  >
                    <div className={`position-absolute top-0 start-0 h-100 w-4px ${isResolved ? "bg-success" : isHigh ? "bg-danger" : "bg-warning"}`}></div>

                    <div className="d-flex align-items-center me-4" style={{ width: "420px", flexShrink: 0 }}>
                      <div className="symbol symbol-48px symbol-circle me-4 flex-shrink-0 d-flex align-items-center justify-content-center bg-light border border-gray-200" style={{ width: 48, height: 48 }}>
                        <i className="ki-outline ki-wrench fs-2 text-warning"></i>
                      </div>
                      <div className="d-flex flex-column overflow-hidden min-w-0">
                        <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                          <span className="fs-6 fw-bold text-gray-900 text-truncate">{r.title}</span>
                          {r.category && (
                            <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2.5 py-0.5">
                              {r.category}
                            </span>
                          )}
                          <StatusChip color={pColor}>{r.priorityLevel || "Medium"}</StatusChip>
                        </div>
                        <div className="text-gray-500 fs-8 text-truncate">
                          {r.description || "No description provided."}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                      <div className="d-none d-md-block text-start">
                        <div className="d6m-muted fs-9">Property</div>
                        <div className="fs-8 fw-semibold text-gray-900">{r.buildingName || "—"}</div>
                      </div>
                      <div className="d-none d-sm-block text-start">
                        <div className="d6m-muted fs-9">Date Logged</div>
                        <div className="fs-8 fw-semibold text-gray-900">{fmtDate(r.createdDate)}</div>
                      </div>
                      <div>
                        <StatusChip color={sColor}>{r.status || "Created"}</StatusChip>
                      </div>
                      <button
                        type="button"
                        className="btn d6-icon-btn"
                        onClick={(e) => { e.stopPropagation(); handleOpenDetail(r); }}
                        title="View details & conversation"
                      >
                        <i className="ki-outline ki-message-text-2 fs-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Pagination ─── */}
          {totalCount > PAGE_SIZE && (
            <div className="d-flex align-items-center justify-content-between pt-5 mt-4 border-top flex-wrap gap-2">
              <span className="text-gray-500 fs-8">
                Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} requests
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light d6-mbtn btn-sm"
                  disabled={page === 0}
                  onClick={() => { const p = page - 1; setPage(p); fetchAll(p); }}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-light d6-mbtn btn-sm"
                  disabled={(page + 1) * PAGE_SIZE >= totalCount}
                  onClick={() => { const p = page + 1; setPage(p); fetchAll(p); }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Modals ─── */}
      <NewRequestModal
        modalRef={newRequestModalRef}
        categories={categories}
        buildings={buildings}
        onSave={handleNewRequest}
      />

      <RequestModal
        modalRef={detailModalRef}
        request={selected ? normalizeRequest(selected) : null}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
